"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateFreightDescriptionFromFreight = exports.calculateTotalWeightForAllFreight = exports.getTotalPalletSpaces = exports.getTotalWeightPounds = exports.hasHazmat = exports.getWaypointOfType = exports.isHomeDelivery = exports.getRouteKeys = void 0;
const utils_ts_1 = require("@mothership/utils-ts");
const models_1 = require("../models");
const STACKABLE_FREIGHT_TYPES = [models_1.FreightType.Box];
const STANDARD_PALLET_LENGTH_INCHES = 42;
const STANDARD_PALLET_WIDTH_INCHES = 48;
const MAX_STACKABLE_HEIGHT_INCHES = 75;
/**
 * Gets all route keys for routes the shipment is currently in.
 *
 * NOTE: A shipment should not have >1 true route key, but we'll return all true route
 * keys in case there is > 1 and clients can decide how to handle these situations.
 */
const getRouteKeys = (shipment) => {
    var _a, _b;
    const routeKeys = utils_ts_1.objectUtils.getTruthyKeys((_b = (_a = shipment.metadata) === null || _a === void 0 ? void 0 : _a.routeKeys) !== null && _b !== void 0 ? _b : {});
    return routeKeys;
};
exports.getRouteKeys = getRouteKeys;
/**
 * Determines whether shipment is a home delivery.
 *
 * @param shipment - The shipment being checked for home delivery.
 * @returns True if the shipment is a home delivery, false otherwise.
 */
const isHomeDelivery = (shipment) => {
    const waypoints = utils_ts_1.nilUtils.assertNotNilOrEmpty(shipment.waypoints, `Waypoints not found on shipment ${shipment.id}.`);
    return waypoints.some((w) => w.accessorialHomeDelivery);
};
exports.isHomeDelivery = isHomeDelivery;
/**
 * Gets waypoint of the specified type from the given shipment.
 *
 * @param shipment - The shipment to get waypoint from.
 * @param waypointType - The type of waypoint to get.
 * @returns Waypoint of the specified type from the given shipment.
 */
const getWaypointOfType = (shipment, waypointType) => {
    var _a;
    const waypoint = utils_ts_1.nilUtils.assertNotNil((_a = shipment.waypoints) === null || _a === void 0 ? void 0 : _a.find((w) => w.type === waypointType), `Waypoint of type ${waypointType} not found on shipment ${shipment.id}.`);
    return waypoint;
};
exports.getWaypointOfType = getWaypointOfType;
/**
 * Determines whether a shipment has hazmat in its freight.
 *
 * @param shipment - The shipment to check.
 * @returns True if the shipment has hazmat in its freight, false otherwise.
 */
const hasHazmat = (shipment) => {
    const freight = utils_ts_1.nilUtils.assertNotNilOrEmpty(shipment.freight, `Freight not found on on shipment ${shipment.id}.`);
    const hasHazmat = freight.some((freightItem) => freightItem.hazmat);
    return hasHazmat;
};
exports.hasHazmat = hasHazmat;
/**
 * Gets the total weight (in pounds) of a shipment.
 *
 * @param shipment - The shipment to calculate the total weight for.
 * @returns The total weight (in pounds) of the shipment.
 */
const getTotalWeightPounds = (shipment) => {
    const freight = utils_ts_1.nilUtils.assertNotNilOrEmpty(shipment.freight, `Freight not found on on shipment ${shipment.id}.`);
    const totalWeightPounds = freight.reduce((total, freightItem) => total + freightItem.weight * freightItem.quantity, 0);
    return totalWeightPounds;
};
exports.getTotalWeightPounds = getTotalWeightPounds;
const _calculateVolumeForStackableFreight = (freightItem) => {
    const volumePerStackableItem = freightItem.width * freightItem.length * freightItem.height;
    const totalVolume = volumePerStackableItem * freightItem.quantity;
    return totalVolume;
};
const _calculateAreaForNonStackableCargo = (freight) => {
    let totalArea = 0;
    const baseAreaPerUnit = freight.width * freight.length;
    const standardPalletArea = STANDARD_PALLET_LENGTH_INCHES * STANDARD_PALLET_WIDTH_INCHES;
    if (freight.type === models_1.FreightType.Pallet &&
        baseAreaPerUnit <= standardPalletArea) {
        // If the selected freight type is pallet, and the dimensions of the freight fit on a standard pallet
        // just use the quantity to determine # of pallets
        totalArea = freight.quantity * standardPalletArea;
    }
    else {
        // Calculate # of pallets needed based on dimensions
        totalArea = baseAreaPerUnit * freight.quantity;
    }
    return totalArea;
};
const _convertVolumeToNumberOfPallets = (volume) => Math.ceil(volume /
    (STANDARD_PALLET_LENGTH_INCHES *
        STANDARD_PALLET_WIDTH_INCHES *
        MAX_STACKABLE_HEIGHT_INCHES));
const _convertAreaToNumberOfPallets = (area) => Math.ceil(area / (STANDARD_PALLET_LENGTH_INCHES * STANDARD_PALLET_WIDTH_INCHES));
/**
 * Gets the total pallet spaces required for a given shipment.
 *
 * @param shipment - The shipment to check.
 * @returns Total pallet spaces required for a given shipment.
 */
const getTotalPalletSpaces = (shipment) => {
    const freight = utils_ts_1.nilUtils.assertNotNil(shipment.freight, `Missing freight from shipment ${shipment.id},`);
    let totalAreaNonStackable = 0;
    let totalVolumeStackable = 0;
    for (const freightItem of freight) {
        if (STACKABLE_FREIGHT_TYPES.includes(freightItem.type)) {
            totalVolumeStackable += _calculateVolumeForStackableFreight(freightItem);
        }
        else {
            totalAreaNonStackable += _calculateAreaForNonStackableCargo(freightItem);
        }
    }
    const totalPalletSpaceForStackable = _convertVolumeToNumberOfPallets(totalVolumeStackable);
    const totalPalletSpaceForNonStackable = _convertAreaToNumberOfPallets(totalAreaNonStackable);
    const totalPalletSpace = totalPalletSpaceForStackable + totalPalletSpaceForNonStackable;
    return totalPalletSpace;
};
exports.getTotalPalletSpaces = getTotalPalletSpaces;
const calculateTotalWeightForAllFreight = (freight) => {
    let totalWeight = 0.0;
    for (const freightData of freight) {
        totalWeight += freightData.weight * freightData.quantity;
    }
    return Math.ceil(totalWeight);
};
exports.calculateTotalWeightForAllFreight = calculateTotalWeightForAllFreight;
// Basic rehashing of cargoCombinedMinifiedString from internal-js-sdk
const generateFreightDescriptionFromFreight = (freight) => {
    var _a;
    const totalWeight = (0, exports.calculateTotalWeightForAllFreight)(freight);
    const freightQuantityMap = {};
    let responseString = '';
    for (const freightData of freight) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const currentQuantity = (_a = freightQuantityMap[freightData.type]) !== null && _a !== void 0 ? _a : 0;
        freightQuantityMap[freightData.type] =
            currentQuantity + freightData.quantity;
    }
    for (let freightType of Object.keys(freightQuantityMap)) {
        const quantity = freightQuantityMap[freightType];
        responseString += quantity;
        if (freightType === models_1.FreightType.LooseItem) {
            freightType = 'Loose Item';
        }
        responseString += ` ${freightType.toLowerCase()}`;
        if (quantity > 1) {
            if (freightType === models_1.FreightType.Box) {
                responseString += 'es';
            }
            else {
                responseString += 's';
            }
        }
        responseString += ', ';
    }
    responseString = responseString.slice(0, -2); // Remove trailing comma and space
    responseString += ` (${totalWeight} lbs)`;
    return responseString;
};
exports.generateFreightDescriptionFromFreight = generateFreightDescriptionFromFreight;
//# sourceMappingURL=shipment.js.map